import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryClient } from '@/app/providers/react-query-provider';

import { apiUserFetch, defaultErrorMsg } from '..';
import { GlobalUserRole } from '../api-error.types';
import { PROFILE_QUERY_KEY } from '../profile/queries';
import { USER_DASHBOARD_QUERY_KEYS } from '../user-dashboard/queries';
import { AUTH_QUERY_KEYS, resetSessionScopedQueries } from './queries';
import { EmailAdviceResponse, QuestionnaireAnswerInput, QuestionnaireSaveRequest } from './types';

export type KycStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type UserGlobalRole = GlobalUserRole;
export type AuthUserResponse = {
  id: string;
  email: string;
  kycStatus: KycStatus;
  entities: {
    entityId: string;
    entityName: string;
    entityRole: string;
    entityApprovalStatus: string;
  }[];
  company: string;
  role: UserGlobalRole;
  name: string;
  isDemo: boolean;
  hasSubmittedQuestionnaire: boolean;
  profileCompleted: boolean;
  referralCode: string | null;
  /**
   * isNonAccredited is true when the user is located in a country that requires
   * accredited-investor status (currently: US) AND does not meet that country's
   * accreditation criteria. false for all other cases — including non-US users
   * and US-accredited users.
   */
  isNonAccredited: boolean;
};

///////////////////////////////////////////////
type KycTokenRes = {
  token: string;
};
export const _setKycToken = () => {
  return apiUserFetch<KycTokenRes>('/kyc/token', {
    method: 'POST',
  });
};
///////////////////////////////////////////////
export const _saveQuestionnaire = (body: QuestionnaireSaveRequest) => {
  return apiUserFetch('/questionnaire/save', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [AUTH_QUERY_KEYS.questionnaire] });
    },
    onResponseError: ({ response }) => {
      defaultErrorMsg(response);
    },
  });
};

export const _recordQuestionnaireAnswers = (body: { answers: QuestionnaireAnswerInput[] }) => {
  return apiUserFetch('/questionnaire/answers', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [AUTH_QUERY_KEYS.questionnaireQuestions] });
    },
    onResponseError: ({ response }) => {
      defaultErrorMsg(response);
    },
  });
};

export const _submitQuestionnaire = () => {
  // Do not invalidate userProfile or questionnaireStatus here: the Questionnaire page redirects as
  // soon as either reports the questionnaire is finished, so it invalidates both itself only after
  // it has switched to the All set screen.
  return apiUserFetch('/questionnaire/submit', {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [AUTH_QUERY_KEYS.questionnaire],
      });
    },
  });
};

export const _resubmitQuestionnaire = (body: QuestionnaireSaveRequest) => {
  return apiUserFetch('/questionnaire/resubmit', {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [AUTH_QUERY_KEYS.questionnaire],
      });
      queryClient.invalidateQueries({
        queryKey: [AUTH_QUERY_KEYS.questionnaireStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile],
      });
      queryClient.invalidateQueries({
        queryKey: PROFILE_QUERY_KEY.profile,
      });
    },
  });
};

export interface SignInExternalRequest {
  provider: 'Cognito';
  /** Cognito ID token (JWT) */
  token: string;
  mfaLoginProof?: string;
  /** Referral code for new user registration (optional) */
  referralCode?: string;
}

export interface SignInExternalResponse extends AuthUserResponse {
  isNewUser?: boolean;
}

export const signInExternal = (body: SignInExternalRequest) =>
  apiUserFetch<SignInExternalResponse>('/auth/sign-in-external', {
    method: 'POST',
    body,
  });

export type SignUpEligibilityRequest = {
  email: string;
};

export type SignUpEligibilityResponse = {
  eligible: boolean;
};

export type BootstrapCognitoMigrationRequest = {
  email: string;
  password: string;
};

export const bootstrapCognitoMigration = (body: BootstrapCognitoMigrationRequest) =>
  apiUserFetch<void>('/auth/migration', {
    method: 'POST',
    body,
  });

export const _checkSignUpEligibility = (body: SignUpEligibilityRequest) =>
  apiUserFetch<SignUpEligibilityResponse>('/auth/eligibility', {
    method: 'POST',
    body,
  });

export const getEmailAdvice = (email: string, signal: AbortSignal) =>
  apiUserFetch<EmailAdviceResponse>('/auth/email-advice', {
    method: 'POST',
    body: { email },
    signal,
  });

export const useSignInExternalMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signInExternal,
    onSuccess: () => {
      // Reset queries to clear stale null and force refetch for active observers
      resetSessionScopedQueries(queryClient);
    },
  });
};
